import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { TranslatableEditor } from '../../../Translations/Inputs';
import { containsKeyPrefix, emailRegex, getServerErrors } from '../../../../common/helpers';
import { editorOptions } from '../../../../common/Tiptap/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import MailEditorLayout from '../../../Common/MailEditorLayout';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

export const replacementKeys = ['first_name', 'last_name', 'full_name', 'email'];

const editEventRegistrationSchema = yup.object({
  notification_emails: yup.array().max(5).of(
    yup.string().matches(emailRegex, { name: 'email' }),
  ),
});

export interface EditEventRegistrationFormProps {
  event: Event;
}

const EditEventRegistrationForm = ({ event }: EditEventRegistrationFormProps) => {
  const { t } = useTranslation();

  const getValues = (event: Event) => ({
    checkout_description: event.checkout_description,
    checkout_description_translations: translationsForAttribute(event.translations, 'checkout_description'),
    confirmation_text: event.confirmation_text,
    confirmation_text_translations: translationsForAttribute(event.translations, 'confirmation_text'),
    confirmation_mail_text: event.confirmation_mail_text,
    confirmation_mail_text_translations: translationsForAttribute(event.translations, 'confirmation_mail_text'),
    notification_emails: event.notification_emails.join(', '),
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const notificationEmails = form.values.notification_emails.split(',').map(
    (email) => email.trim(),
  ).filter((email) => !!email);

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      registration: {
        ...form.values,
        notification_emails: notificationEmails,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventRegistrationSchema,
    values: {
      ...form.values,
      notification_emails: notificationEmails,
    },
    externalErrors: getServerErrors(error, 'registration'),
  });

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} sc={{ mb: 4 }} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('registration')}</UI.Legend>
          <UI.InputDescription>
            {t('event_registration_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup
          sc={{ valid: !errors.checkout_description, touched: form.touched.checkout_description }}
        >
          <UI.InputLabel id="EventCheckoutDescriptionLabel">
            {t('registration_form_description')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('registration_form_description_description')}
          </UI.InputDescription>
          <TranslatableEditor
            form={form}
            errors={errors}
            name="checkout_description"
            title={t('registration_form_description')}
            defaultLocale={event.locale}
            brandColor={event.brand_color}
            attributes={{
              'aria-labelledby': 'EventCheckoutDescriptionLabel',
            }}
          />
          <UI.ErrorMessages
            attribute={t('registration_form_description')}
            errors={errors.checkout_description}
          />
        </UI.InputGroup>

        <UI.InputGroup
          sc={{ valid: !errors.confirmation_text, touched: form.touched.confirmation_text }}
        >
          <UI.InputLabel id="EventConfirmationTextLabel">
            {t('registration_confirmation')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('registration_confirmation_description')}
          </UI.InputDescription>
          <TranslatableEditor
            form={form}
            errors={errors}
            name="confirmation_text"
            title={t('registration_confirmation')}
            defaultLocale={event.locale}
            brandColor={event.brand_color}
            attributes={{
              'aria-labelledby': 'EventConfirmationTextLabel',
            }}
          />
          <UI.ErrorMessages
            attribute={t('registration_confirmation')}
            errors={errors.confirmation_text}
          />
        </UI.InputGroup>

        <UI.InputGroup
          sc={{ valid: !errors.confirmation_mail_text, touched: form.touched.confirmation_mail_text }}
        >
          <UI.InputLabel id="EventConfirmationMailLabel">
            {t('confirmation_mail')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('confirmation_mail_text_description')}
          </UI.InputDescription>
          <MailEditorLayout
            editor={(props) => (
              <TranslatableEditor
                form={form}
                errors={errors}
                name="confirmation_mail_text"
                defaultLocale={event.locale}
                title={t('confirmation_mail')}
                variables={replacementKeys}
                options={editorOptions.full}
                attributes={{
                  'aria-labelledby': 'EventConfirmationMailLabel',
                }}
                {...props}
              />
            )}
            value={form.values.confirmation_mail_text}
            event={event}
            showRegistrationSummary
            showOrderSummary
          />
          <UI.ErrorMessages
            attribute={t('confirmation_mail')}
            errors={errors.confirmation_mail_text}
          />
        </UI.InputGroup>

        <UI.InputGroup
          sc={{
            valid: !containsKeyPrefix(errors, 'notification_emails'),
            touched: form.touched.notification_emails,
          }}
        >
          <UI.InputLabel htmlFor="email_notifications">
            {t('email_notifications')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('email_notification_description')}
          </UI.InputDescription>
          <UI.DebouncedInput {...form.text('notification_emails')} id="email_notifications" />
          <UI.ErrorMessages
            attribute={t('email_notifications')}
            errors={errors.notification_emails}
          />
          {notificationEmails.map((email, index) => (
            <UI.ErrorMessages
              attribute={`${t('email_address')} ${index + 1}`}
              errors={errors[`notification_emails.${index}`]}
              key={index}
            />
          ))}
        </UI.InputGroup>

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventRegistrationForm;
